import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, MessageCircle, Phone, Radio, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, STATUS, vehicleLabel, waLink } from "@/lib/api";
import { StatusBadge } from "@/components/Ticket";
import DocumentRepository from "@/components/DocumentRepository";

const VFIELDS = [
  ["plate", "Targa"], ["vin", "VIN"], ["make", "Marca"], ["model", "Modello"], ["year", "Anno"],
  ["displacement", "Cilindrata"], ["power_kw", "Potenza kW"], ["fuel", "Carburante"], ["engine_code", "Cod. Motore"], ["engine", "Motorizzazione"],
];

function VehicleGrid({ v }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-[#383838] border border-[#383838] rounded-md overflow-hidden" data-testid="ticket-vehicle-grid">
      {VFIELDS.map(([k, l]) => (
        <div key={k} className="bg-[#1E1E1E] p-3">
          <div className="label !mb-1">{l}</div>
          <div className={`mono text-sm break-all ${v[k] ? "text-white" : "text-slate-600"}`} data-testid={`ticket-vehicle-${k}`}>{v[k] || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function AdminControls({ ticket, setTicket }) {
  const [notes, setNotes] = useState(ticket.admin_notes || "");
  const v = ticket.vehicle;
  const liveText = `[Supporto Tecnico] Avvio sessione live per Ticket #${ticket.number} - Veicolo: ${vehicleLabel(v)} - Targa: ${v.plate || "N/D"} - VIN: ${v.vin || "N/D"}`;
  const setStatus = async (status) => {
    try {
      const { data } = await api.patch(`/tickets/${ticket.id}/status`, { status });
      setTicket(data);
      toast.success(`Stato: ${STATUS[status].label}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const saveNotes = async () => {
    const { data } = await api.patch(`/tickets/${ticket.id}/notes`, { admin_notes: notes });
    setTicket(data);
    toast.success("Note salvate");
  };
  return (
    <div className="surface p-5 space-y-5" data-testid="admin-controls">
      <a href={waLink(ticket.client_phone, liveText)} target="_blank" rel="noreferrer"
        onClick={() => ticket.status === "in_coda" && setStatus("in_lavorazione")}
        className="btn-wa w-full py-3.5 flex items-center justify-center gap-2" data-testid="start-whatsapp-live-button">
        <Radio size={18} /> Avvia Sessione Live WhatsApp
      </a>
      <div>
        <div className="label">Stato ticket</div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(STATUS).map(([k, s]) => (
            <button key={k} onClick={() => setStatus(k)} data-testid={`set-status-${k}`}
              className={`text-xs px-2 py-2.5 rounded-sm border transition-colors ${ticket.status === k ? `${s.cls} font-semibold` : "border-[#383838] text-slate-400 hover:text-white"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="label">Note interne tecnico</div>
        <textarea className="field min-h-[90px] text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-notes-input" />
        <button onClick={saveNotes} className="btn-ghost mt-2 px-3 py-2 text-xs flex items-center gap-1.5" data-testid="save-notes-button"><Save size={14} /> Salva note</button>
      </div>
      <div className="text-sm space-y-1 border-t border-[#383838] pt-4">
        <div className="label">Cliente</div>
        <div className="text-white font-semibold" data-testid="ticket-client-name">{ticket.client_name}</div>
        {ticket.client_company && <div className="text-slate-400">{ticket.client_company}</div>}
        <div className="text-slate-400 mono flex items-center gap-1.5"><Phone size={13} /> {ticket.client_phone}</div>
        <div className="text-slate-400">{ticket.client_email}</div>
      </div>
    </div>
  );
}

function ClientContact({ ticket }) {
  const [num, setNum] = useState("");
  useEffect(() => {
    api.get("/config").then(({ data }) => setNum(data.admin_whatsapp));
  }, []);
  const text = `[Supporto Tecnico] Ticket #${ticket.number} - Veicolo: ${vehicleLabel(ticket.vehicle)} - Targa: ${ticket.vehicle.plate || "N/D"}`;
  return (
    <div className="surface p-5 space-y-3" data-testid="client-contact">
      <div className="label">Sessione live</div>
      <p className="text-sm text-slate-400">All'orario prenotato il tecnico ti contatterà su WhatsApp al numero <span className="mono text-white">{ticket.client_phone}</span>.</p>
      <p className="text-xs text-slate-400" data-testid="reminder-info">
        {ticket.reminder_sent ? "Promemoria email inviato." : `Riceverai un promemoria email a ${ticket.client_email} circa un'ora prima.`}
      </p>
      {num && (
        <a href={waLink(num, text)} target="_blank" rel="noreferrer" className="btn-wa w-full py-3 flex items-center justify-center gap-2" data-testid="contact-tech-whatsapp-button">
          <MessageCircle size={18} /> Scrivi al tecnico su WhatsApp
        </a>
      )}
    </div>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState("");
  const isAdmin = user.role === "admin";
  useEffect(() => {
    api.get(`/tickets/${id}`).then(({ data }) => setTicket(data)).catch((e) => setError(errMsg(e)));
  }, [id]);
  if (error) return <div className="text-red-400" data-testid="ticket-error">{error}</div>;
  if (!ticket) return <div className="text-slate-500">Caricamento…</div>;
  return (
    <div className="space-y-6">
      <Link to={isAdmin ? "/admin" : "/dashboard"} className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5 w-fit" data-testid="back-link">
        <ArrowLeft size={15} /> {isAdmin ? "Coda di lavoro" : "Le tue richieste"}
      </Link>
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div>
          <div className="eyebrow mb-2">Scheda ticket</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase" data-testid="ticket-title">
            <span className="text-[#E50914]">#{ticket.number}</span> {vehicleLabel(ticket.vehicle)}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-slate-300">
            <StatusBadge status={ticket.status} />
            <span className="flex items-center gap-1.5 capitalize" data-testid="ticket-slot"><CalendarClock size={15} /> {fmtDate(ticket.slot_date)} · <span className="mono">{ticket.slot_time}</span></span>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-6 min-w-0">
          <VehicleGrid v={ticket.vehicle} />
          <div className="surface p-5 sm:p-6">
            <div className="label">Descrizione difetto</div>
            <p className="text-slate-200 whitespace-pre-wrap leading-relaxed" data-testid="ticket-description">{ticket.description}</p>
            {ticket.dtc_codes.length > 0 && (
              <div className="mt-5">
                <div className="label">Codici DTC</div>
                <div className="flex flex-wrap gap-2" data-testid="ticket-dtc-list">
                  {ticket.dtc_codes.map((c) => <span key={c} className="tag text-[#FF4D4D] !border-red-500/40">{c}</span>)}
                </div>
              </div>
            )}
          </div>
          <div className="surface p-5 sm:p-6"><DocumentRepository ticket={ticket} isAdmin={isAdmin} /></div>
        </div>
        <div className="space-y-4 lg:sticky lg:top-24 self-start">
          {isAdmin ? <AdminControls ticket={ticket} setTicket={setTicket} /> : <ClientContact ticket={ticket} />}
        </div>
      </div>
    </div>
  );
}
